import type { LevelDef } from '../sim/level/schema';
import type { ThemePalette } from '../sim/level/themes';
import { Layer, group, type ShapeGroup } from './frame';
import { PRIM_CAPSULE, PRIM_DISK, PRIM_ROUNDED_BOX, PRIM_TRIANGLE, type Primitive, withAlpha } from './sdf/primitives';

/**
 * Backdrop decorations: themed silhouettes behind the arena with parallax.
 * Levels may author `decor`; otherwise a deterministic set is generated from the level id so
 * every arena has a backdrop without touching level data.
 */
export type DecorItem = { kind: string; x: number; y: number; scale: number; depth: number; seed: number };

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function rng(seed: number): () => number {
  let s = seed || 1;
  return () => {
    s ^= s << 13;
    s >>>= 0;
    s ^= s >>> 17;
    s ^= s << 5;
    s >>>= 0;
    return s / 4294967296;
  };
}

const cache = new Map<string, DecorItem[]>();

export function decorForLevel(level: LevelDef, theme: ThemePalette): DecorItem[] {
  const key = `${level.id}:${theme.id}:${level.decor?.length ?? 0}`;
  const cached = cache.get(key);
  if (cached) return cached;
  const items: DecorItem[] = [];
  const b = level.bounds;
  const authored = level.decor ?? [];
  authored.forEach((d, i) => {
    const attached = ['office-', 'pirate-', 'neon-'].some((prefix) => d.kind.startsWith(prefix)) || ['heart-tree', 'cargo-frame', 'courtyard-arch'].includes(d.kind);
    const depth = d.kind === 'pirate-island' ? 0.3 : attached ? 1 : 0.55;
    items.push({ kind: d.kind, x: d.x, y: d.y, scale: d.scale ?? 1, depth, seed: i * 7919 });
  });
  const next = rng(hashStr(level.id));
  const kinds = theme.decorKinds;
  const floor = b.y + 0.5;
  // Far band: big soft shapes low on the horizon.
  const farCount = 4 + Math.floor(next() * 3);
  for (let i = 0; i < farCount; i++) {
    const kind = pickFar(kinds);
    if (!kind) break;
    items.push({
      kind,
      x: b.x - 4 + next() * (b.w + 8),
      y: floor - 1,
      scale: 1.6 + next() * 2.2,
      depth: 0.3,
      seed: Math.floor(next() * 1e6),
    });
  }
  // Near band: medium silhouettes spread across the width.
  const nearCount = 5 + Math.floor(next() * 4);
  for (let i = 0; i < nearCount; i++) {
    const kind = pickNear(kinds, next);
    if (!kind) break;
    items.push({
      kind,
      x: b.x - 2 + next() * (b.w + 4),
      y: floor,
      scale: 0.9 + next() * 1.1,
      depth: 0.55,
      seed: Math.floor(next() * 1e6),
    });
  }
  // Sky objects.
  for (const kind of kinds) {
    if (kind === 'moon' || kind === 'sun') {
      items.push({ kind, x: b.x + b.w * (0.15 + next() * 0.7), y: b.y + b.h * 0.8, scale: 1 + next() * 0.6, depth: 0.15, seed: 1 });
    }
    if (kind === 'star') {
      for (let i = 0; i < 26; i++) {
        items.push({ kind, x: b.x - 4 + next() * (b.w + 8), y: b.y + b.h * (0.35 + next() * 0.7), scale: 0.4 + next() * 0.8, depth: 0.1, seed: i });
      }
    }
    if (kind === 'cloud') {
      for (let i = 0; i < 4; i++) {
        items.push({ kind, x: b.x - 4 + next() * (b.w + 8), y: b.y + b.h * (0.55 + next() * 0.4), scale: 1 + next() * 1.4, depth: 0.2, seed: i });
      }
    }
    if (kind === 'bat') {
      for (let i = 0; i < 5; i++) {
        items.push({ kind, x: b.x + next() * b.w, y: b.y + b.h * (0.5 + next() * 0.4), scale: 0.5 + next() * 0.5, depth: 0.45, seed: i });
      }
    }
    if (kind === 'ember') {
      for (let i = 0; i < 18; i++) {
        items.push({ kind, x: b.x + next() * b.w, y: b.y + next() * b.h, scale: 0.4 + next() * 0.8, depth: 0.5, seed: i });
      }
    }
    if (kind === 'snow') {
      for (let i = 0; i < 40; i++) {
        items.push({ kind, x: b.x - 4 + next() * (b.w + 8), y: b.y + next() * b.h, scale: 0.6 + next() * 0.8, depth: 0.6, seed: i });
      }
    }
    if (kind === 'grid') {
      items.push({ kind, x: b.x + b.w / 2, y: b.y + b.h / 2, scale: Math.max(b.w, b.h), depth: 0.4, seed: 0 });
    }
  }
  // A few large landmarks give each biome its own architecture without cluttering the fight plane.
  if (theme.id === 'woods') {
    for (const fraction of [0.12, 0.86]) items.push({ kind: 'canopy', x: b.x + b.w * fraction, y: b.y + b.h * 0.65, scale: 1.5, depth: 0.4, seed: fraction * 100 });
    items.push({ kind: 'sunbeam', x: b.x + b.w * 0.35, y: b.y + b.h, scale: b.h * 0.8, depth: 0.1, seed: 0 });
  } else if (theme.id === 'factory') {
    for (const fraction of [0.22, 0.78]) items.push({ kind: 'gantry', x: b.x + b.w * fraction, y: b.y + 1, scale: 1.5, depth: 0.45, seed: fraction * 100 });
  } else if (theme.id === 'castle') {
    for (const fraction of [0.25, 0.75]) {
      items.push({ kind: 'banner', x: b.x + b.w * fraction, y: b.y + b.h * 0.56, scale: 1.25, depth: 0.48, seed: fraction * 100 });
      items.push({ kind: 'torch', x: b.x + b.w * fraction, y: b.y + b.h * 0.3, scale: 0.8, depth: 0.5, seed: fraction * 100 });
    }
  }
  cache.set(key, items);
  return items;
}

function pickFar(kinds: string[]): string | undefined {
  for (const k of ['hill', 'dune', 'mesa', 'stack', 'tower', 'stalagmite']) if (kinds.includes(k)) return k;
  return undefined;
}
function pickNear(kinds: string[], next: () => number): string | undefined {
  const near = kinds.filter((k) => ['pine', 'tree', 'bush', 'cactus', 'gear', 'pipe', 'tower', 'grave', 'stalagmite', 'stack'].includes(k));
  if (!near.length) return undefined;
  return near[Math.floor(next() * near.length)];
}

/** Build backdrop groups for one frame, with parallax relative to the level center. */
export function decorGroups(
  items: DecorItem[],
  theme: ThemePalette,
  cam: { x: number; y: number },
  center: { x: number; y: number },
  time: number,
): ShapeGroup[] {
  const out: ShapeGroup[] = [];
  for (const d of items) {
    const px = d.x + (cam.x - center.x) * (1 - d.depth) * 0.6;
    const py = d.y + (cam.y - center.y) * (1 - d.depth) * 0.35;
    const color = d.depth < 0.45 ? theme.decorFar : theme.decor;
    const shapes = decorShapes(d.kind, px, py, d.scale, d.seed, time, theme, color);
    for (const g of shapes) out.push(g);
  }
  return out;
}

function disk(cx: number, cy: number, r: number): Primitive {
  return { kind: PRIM_DISK, ax: cx, ay: cy, bx: cx, by: cy, r };
}
function cap(ax: number, ay: number, bx: number, by: number, r: number): Primitive {
  return { kind: PRIM_CAPSULE, ax, ay, bx, by, r };
}
function rbox(cx: number, cy: number, hx: number, hy: number, r = 0.05, rot = 0): Primitive {
  return { kind: PRIM_ROUNDED_BOX, ax: cx, ay: cy, bx: hx, by: hy, r, rot };
}
function tri(cx: number, cy: number, halfBase: number, height: number, rot = 0, r = 0.04): Primitive {
  return { kind: PRIM_TRIANGLE, ax: cx, ay: cy, bx: halfBase, by: height, r, rot };
}

function decorShapes(kind: string, x: number, y: number, s: number, seed: number, time: number, theme: ThemePalette, color: string): ShapeGroup[] {
  const L = Layer.Decor;
  switch (kind) {
    case 'pirate-sea': {
      const waves: Primitive[] = [];
      for (let i = 0; i < 8; i++) {
        const wx = x + (i * 4.7 - 17) * s + Math.sin(time * 0.35 + i) * 0.12;
        waves.push(cap(wx, y - (i % 3) * 0.55 * s, wx + (2.3 + i % 2) * s, y - (i % 3) * 0.55 * s, 0.025 * s));
      }
      return [
        group([rbox(x, y - 3.6 * s, 23 * s, 4 * s, 0)], '#587f8c', L, { style: 'flat' }),
        group(waves, withAlpha('#c2d1c6', 0.5), L, { style: 'flat' }),
      ];
    }
    case 'pirate-island':
      return [
        group([tri(x, y, 4.8 * s, 4.1 * s, 0, 0.5 * s), tri(x + 3 * s, y, 3.8 * s, 2.7 * s, 0, 0.5 * s)], withAlpha(theme.decorFar, 0.65), L, { style: 'flat' }),
        group([cap(x, y + 1.7 * s, x + 0.3 * s, y + 5.4 * s, 0.08 * s), cap(x + 0.3 * s, y + 5.4 * s, x - 1.3 * s, y + 5.1 * s, 0.18 * s), cap(x + 0.3 * s, y + 5.4 * s, x + 1.8 * s, y + 5 * s, 0.18 * s)], withAlpha(theme.decorFar, 0.7), L, { style: 'flat' }),
      ];
    case 'pirate-hull': {
      const ribs = [-11, -7, -3, 3, 7, 11].map((dx) => cap(x + dx * s, y - 2.5 * s, x + dx * s, y - 0.2 * s, 0.035 * s));
      return [
        group([rbox(x, y - 0.95 * s, 14.5 * s, 1.9 * s, 1.7 * s)], '#565464', L, { style: 'flat' }),
        group([cap(x - 13.5 * s, y - 0.55 * s, x + 13.5 * s, y - 0.55 * s, 0.07 * s), ...ribs], '#878076', L, { style: 'flat' }),
      ];
    }
    case 'pirate-mast': {
      const pole = [cap(x, y, x, y + 12 * s, 0.17 * s), cap(x - 4.9 * s, y + 9.9 * s, x + 4.9 * s, y + 9.9 * s, 0.11 * s)];
      const sail = [rbox(x, y + 7.8 * s, 4.5 * s, 2 * s, 0.6 * s), tri(x + 1.15 * s, y + 11.7 * s, 0.75 * s, 2.3 * s, -Math.PI / 2)];
      const emblem = [disk(x, y + 8.25 * s, 0.43 * s), rbox(x, y + 7.92 * s, 0.23 * s, 0.22 * s), cap(x - 0.7 * s, y + 7.2 * s, x + 0.7 * s, y + 7.7 * s, 0.07 * s), cap(x - 0.7 * s, y + 7.7 * s, x + 0.7 * s, y + 7.2 * s, 0.07 * s)];
      return [
        group(sail, withAlpha('#3e4b5a', 0.72), L, { style: 'flat' }),
        group(pole, withAlpha('#77736d', 0.76), L, { style: 'flat' }),
        group(emblem, withAlpha('#b6b8aa', 0.56), L, { style: 'flat' }),
        group([disk(x - 0.16 * s, y + 8.25 * s, 0.09 * s), disk(x + 0.16 * s, y + 8.25 * s, 0.09 * s)], '#5c6870', L, { style: 'flat' }),
      ];
    }
    case 'pirate-rigging': {
      const ropes = [cap(x - 11 * s, y + 2.4 * s, x, y + 10.4 * s, 0.022 * s), cap(x + 11 * s, y + 2.4 * s, x, y + 10.4 * s, 0.022 * s), cap(x - 0.65 * s, y + 2 * s, x - 0.32 * s, y + 10 * s, 0.024 * s), cap(x + 0.65 * s, y + 2 * s, x + 0.32 * s, y + 10 * s, 0.024 * s)];
      for (let i = 0; i < 9; i++) ropes.push(cap(x - (0.65 - i * 0.035) * s, y + (2.3 + i * 0.85) * s, x + (0.65 - i * 0.035) * s, y + (2.3 + i * 0.85) * s, 0.02 * s));
      return [group(ropes, withAlpha('#afb3a7', 0.48), L, { style: 'flat' })];
    }
    case 'pirate-dock': {
      const piles = [-16, -11, -5, 5, 11, 16].map((dx) => cap(x + dx * s, y - 2.4 * s, x + dx * s, y + 0.2 * s, 0.22 * s));
      return [
        group(piles, '#5e6570', L, { style: 'flat' }),
        group([cap(x - 17 * s, y - 0.75 * s, x + 17 * s, y - 0.75 * s, 0.14 * s)], '#7f8078', L, { style: 'flat' }),
      ];
    }
    case 'pirate-warehouse':
      return [
        group([rbox(x, y + 3.5 * s, 4.7 * s, 3.5 * s, 0.08 * s), tri(x, y + 7 * s, 5.3 * s, 2.5 * s)], withAlpha('#77848a', 0.82), L, { style: 'flat' }),
        group([cap(x - 4.4 * s, y + 6.8 * s, x + 4.4 * s, y + 6.8 * s, 0.065 * s), cap(x - 3.4 * s, y, x - 3.4 * s, y + 6.8 * s, 0.055 * s), cap(x + 3.4 * s, y, x + 3.4 * s, y + 6.8 * s, 0.055 * s)], withAlpha('#abb4aa', 0.5), L, { style: 'flat' }),
        group([rbox(x - 1.6 * s, y + 5.3 * s, 0.65 * s, 0.7 * s, 0.05), rbox(x + 1.6 * s, y + 5.3 * s, 0.65 * s, 0.7 * s, 0.05)], withAlpha('#d9c69c', 0.55), L, { style: 'flat' }),
      ];
    case 'pirate-lantern':
      return [
        group([cap(x, y + 1.5 * s, x, y + 0.45 * s, 0.025 * s), rbox(x, y, 0.26 * s, 0.4 * s, 0.06 * s)], '#697582', L, { style: 'flat' }),
        group([rbox(x, y, 0.15 * s, 0.25 * s, 0.03 * s)], '#e7c58e', L, { style: 'flat', fx: 'glow', glow: 0.18 * s }),
      ];
    case 'neon-grid': {
      // Individual strips have narrow bounds, avoiding a full-screen bundle of SDF evaluations.
      const lines: ShapeGroup[] = [];
      for (let i = -6; i <= 6; i++) lines.push(group([cap(x + i * 3 * s, y - 1 * s, x + i * 3 * s, y + 13 * s, 0.012 * s)], withAlpha('#69a1b5', 0.11), L, { style: 'flat', pad: 0.03 }));
      for (let i = 0; i < 6; i++) lines.push(group([cap(x - 19 * s, y + i * 2.4 * s, x + 19 * s, y + i * 2.4 * s, 0.012 * s)], withAlpha('#69a1b5', 0.11), L, { style: 'flat', pad: 0.03 }));
      return lines;
    }
    case 'neon-portal': {
      const frame = [cap(x - 6.5 * s, y, x - 6.5 * s, y + 9 * s, 0.35 * s), cap(x - 6.5 * s, y + 9 * s, x - 4.2 * s, y + 11.3 * s, 0.35 * s), cap(x - 4.2 * s, y + 11.3 * s, x + 4.2 * s, y + 11.3 * s, 0.35 * s), cap(x + 4.2 * s, y + 11.3 * s, x + 6.5 * s, y + 9 * s, 0.35 * s), cap(x + 6.5 * s, y + 9 * s, x + 6.5 * s, y, 0.35 * s)];
      const rails = frame.map((line) => ({ ...line, r: 0.035 * s }));
      return [
        group(frame, '#314b63', L, { style: 'flat' }),
        group(rails, withAlpha('#85bac9', 0.48), L, { style: 'flat' }),
        group([rbox(x, y + 11.3 * s, 1.7 * s, 0.13 * s, 0.03 * s)], withAlpha('#b79bdc', 0.7), L, { style: 'flat' }),
      ];
    }
    case 'neon-pylon':
      return [
        group([rbox(x, y + 5.5 * s, 0.85 * s, 5.5 * s, 0.12 * s)], '#2a4057', L, { style: 'flat' }),
        group([cap(x - 0.46 * s, y + 2 * s, x - 0.46 * s, y + 8.6 * s, 0.025 * s), cap(x + 0.46 * s, y + 3 * s, x + 0.46 * s, y + 9.6 * s, 0.025 * s)], withAlpha('#74b1c2', 0.55), L, { style: 'flat' }),
        group([rbox(x, y + 10.5 * s, 0.4 * s, 0.12 * s, 0.02)], withAlpha('#aa94c9', 0.7), L, { style: 'flat' }),
      ];
    case 'neon-trace':
      return [group([cap(x - 2.4 * s, y - 0.35 * s, x - 1.2 * s, y - 0.35 * s, 0.025 * s), cap(x - 1.2 * s, y - 0.35 * s, x - 0.6 * s, y - 0.95 * s, 0.025 * s), cap(x - 0.6 * s, y - 0.95 * s, x + 2.4 * s, y - 0.95 * s, 0.025 * s), disk(x + 2.4 * s, y - 0.95 * s, 0.075 * s)], withAlpha('#809fc2', 0.48), L, { style: 'flat' })];
    case 'neon-skyline': {
      const towers: Primitive[] = [], windows: Primitive[] = [];
      for (const [i, dx] of [-15, -10.5, -5.8, 5.8, 10.5, 15].entries()) {
        const height = [9, 12.5, 10, 11, 13.5, 9.5][i]! * s;
        towers.push(rbox(x + dx * s, y + height / 2, 1.6 * s, height / 2, 0.08 * s));
        windows.push(cap(x + (dx - 0.9) * s, y + height - 1.7 * s, x + (dx + 0.2) * s, y + height - 1.7 * s, 0.04 * s), cap(x + (dx - 0.9) * s, y + height - 2.5 * s, x + (dx + 0.7) * s, y + height - 2.5 * s, 0.025 * s));
      }
      return [group(towers, '#243950', L, { style: 'flat' }), group(windows, withAlpha('#8b91b9', 0.45), L, { style: 'flat' })];
    }
    case 'office-wall': {
      return [
        group([rbox(x, y + 7 * s, 18 * s, 7 * s, 0)], '#bbcbd0', L, { style: 'flat' }),
        group([rbox(x, y + 0.75 * s, 18 * s, 0.75 * s, 0)], '#a2b4bf', L, { style: 'flat' }),
        group([cap(x - 18 * s, y + 1.5 * s, x + 18 * s, y + 1.5 * s, 0.035), cap(x - 18 * s, y + 13.5 * s, x + 18 * s, y + 13.5 * s, 0.08)], '#d5dfe0', L, { style: 'flat' }),
      ];
    }
    case 'office-window': {
      const skyline = [rbox(x - 2.15 * s, y - 1.1 * s, 0.65 * s, 1.5 * s), rbox(x - 0.55 * s, y - 0.7 * s, 0.6 * s, 1.9 * s), rbox(x + 1 * s, y - 1.3 * s, 0.7 * s, 1.3 * s), rbox(x + 2.3 * s, y - 0.95 * s, 0.5 * s, 1.65 * s)];
      const frame = [rbox(x, y, 3.45 * s, 2.9 * s, 0.04)];
      const mullions = [cap(x, y - 2.65 * s, x, y + 2.65 * s, 0.075 * s), cap(x - 3.2 * s, y, x + 3.2 * s, y, 0.075 * s), rbox(x, y - 2.85 * s, 3.65 * s, 0.12 * s, 0.025)];
      return [
        group(frame, '#879da9', L, { style: 'flat' }),
        group([rbox(x, y, 3.2 * s, 2.65 * s, 0)], '#d1e3e7', L, { style: 'flat' }),
        group(skyline, '#a8c3ce', L, { style: 'flat' }),
        group([cap(x - 2.7 * s, y + 1.4 * s, x - 1.7 * s, y + 2.3 * s, 0.045 * s), cap(x + 1.7 * s, y + 0.6 * s, x + 2.6 * s, y + 1.4 * s, 0.045 * s)], '#ebf4f3', L, { style: 'flat' }),
        group(mullions, '#eef1e9', L, { style: 'flat' }),
      ];
    }
    case 'office-whiteboard': {
      return [
        group([rbox(x, y, 3.7 * s, 2 * s, 0.1 * s)], '#879aa1', L, { style: 'flat' }),
        group([rbox(x, y + 0.03 * s, 3.55 * s, 1.84 * s, 0.025)], '#e6ebde', L, { style: 'flat' }),
        group([cap(x - 2.9 * s, y + 1.17 * s, x + 0.8 * s, y + 1.17 * s, 0.055 * s), cap(x - 2.9 * s, y + 0.86 * s, x - 1.1 * s, y + 0.86 * s, 0.028 * s), cap(x - 2.8 * s, y - 1.13 * s, x - 2.8 * s, y + 0.25 * s, 0.026), cap(x - 2.8 * s, y - 1.13 * s, x + 0.3 * s, y - 1.13 * s, 0.026)], '#889a9a', L, { style: 'flat' }),
        group([cap(x - 2.6 * s, y - 0.8 * s, x - 1.9 * s, y - 0.5 * s, 0.04 * s), cap(x - 1.9 * s, y - 0.5 * s, x - 1.1 * s, y - 0.7 * s, 0.04 * s), cap(x - 1.1 * s, y - 0.7 * s, x - 0.2 * s, y + 0.25 * s, 0.04 * s)], '#66a39a', L, { style: 'flat' }),
        group([rbox(x + 1.45 * s, y + 0.35 * s, 0.4 * s, 0.4 * s, 0.01, 0.06), rbox(x + 2.5 * s, y - 0.8 * s, 0.4 * s, 0.4 * s, 0.01, -0.08)], '#e0c67f', L, { style: 'flat' }),
        group([rbox(x + 2.5 * s, y + 0.35 * s, 0.4 * s, 0.4 * s, 0.01, -0.035)], '#c9aaa1', L, { style: 'flat' }),
        group([rbox(x, y - 2.02 * s, 3.75 * s, 0.055 * s, 0.01)], '#718995', L, { style: 'flat' }),
      ];
    }
    case 'office-clock':
      return [
        group([disk(x, y, 0.68 * s)], '#84979f', L, { style: 'flat' }),
        group([disk(x, y, 0.58 * s)], '#eceddf', L, { style: 'flat' }),
        group([cap(x, y, x - 0.27 * s, y + 0.21 * s, 0.035 * s), cap(x, y, x + 0.23 * s, y + 0.38 * s, 0.028 * s), disk(x, y, 0.06 * s)], '#758891', L, { style: 'flat' }),
      ];
    case 'office-light':
      return [
        group([cap(x - 1.7 * s, y + 0.65 * s, x - 1.7 * s, y + 0.15 * s, 0.025 * s), cap(x + 1.7 * s, y + 0.65 * s, x + 1.7 * s, y + 0.15 * s, 0.025 * s), rbox(x, y, 2.6 * s, 0.14 * s, 0.035)], '#8b9da5', L, { style: 'flat' }),
        group([rbox(x, y - 0.09 * s, 2.42 * s, 0.045 * s, 0.025)], '#fff3ce', L, { style: 'flat', fx: 'glow', glow: 0.32 * s }),
      ];
    case 'office-desk':
    case 'office-conference': {
      const conference = kind === 'office-conference';
      const width = conference ? 3.5 : 2.55;
      const legs = [cap(x - width * s, y + 0.15 * s, x - width * s, y + 2.2 * s, 0.075 * s), cap(x + width * s, y + 0.15 * s, x + width * s, y + 2.2 * s, 0.075 * s)];
      const chairs: Primitive[] = [];
      for (const dx of conference ? [-1.9, 1.9] : [1.05]) {
        const cx = x + dx * s;
        chairs.push(rbox(cx, y + 1.55 * s, 0.48 * s, 0.62 * s, 0.18 * s), cap(cx - 0.65 * s, y + 0.97 * s, cx + 0.65 * s, y + 0.97 * s, 0.08 * s), cap(cx, y + 0.2 * s, cx, y + 1 * s, 0.055 * s), cap(cx - 0.53 * s, y + 0.13 * s, cx + 0.53 * s, y + 0.13 * s, 0.06 * s));
      }
      return [
        group(chairs, '#92a7b0', L, { style: 'flat' }),
        group(legs, '#7c929e', L, { style: 'flat' }),
        ...(!conference ? [group([rbox(x - 1.75 * s, y + 1.55 * s, 0.55 * s, 0.64 * s, 0.025)], '#98aeb6', L, { style: 'flat' }), group([cap(x - 2.06 * s, y + 1.57 * s, x - 1.44 * s, y + 1.57 * s, 0.025 * s)], '#c3d0d1', L, { style: 'flat' })] : []),
      ];
    }
    case 'office-monitor':
      return [
        group([rbox(x, y + 1.05 * s, 0.77 * s, 0.5 * s, 0.05 * s), cap(x, y + 0.08 * s, x, y + 0.6 * s, 0.06 * s), cap(x - 0.4 * s, y + 0.07 * s, x + 0.4 * s, y + 0.07 * s, 0.06 * s)], '#748993', L, { style: 'flat' }),
        group([rbox(x, y + 1.08 * s, 0.66 * s, 0.36 * s, 0.02)], '#aac9c9', L, { style: 'flat' }),
        group([cap(x - 0.44 * s, y + 1.23 * s, x + 0.2 * s, y + 1.23 * s, 0.025 * s), cap(x - 0.44 * s, y + 1.05 * s, x - 0.03 * s, y + 1.05 * s, 0.02 * s)], '#deeee5', L, { style: 'flat' }),
      ];
    case 'office-papers':
      return [
        group([rbox(x, y + 0.09 * s, 0.62 * s, 0.085 * s, 0.01), rbox(x + 0.08 * s, y + 0.21 * s, 0.55 * s, 0.035 * s, 0.01, -0.035)], '#e4e7d9', L, { style: 'flat' }),
        group([rbox(x + 0.62 * s, y + 0.2 * s, 0.08 * s, 0.19 * s, 0.01)], '#c8ad83', L, { style: 'flat' }),
      ];
    case 'office-plant':
      return [
        group([cap(x, y + 0.5 * s, x, y + 2.2 * s, 0.035 * s)], '#8ba396', L, { style: 'flat' }),
        group([cap(x, y + 1.05 * s, x - 0.45 * s, y + 1.65 * s, 0.18 * s), cap(x, y + 1.3 * s, x + 0.43 * s, y + 1.95 * s, 0.2 * s), cap(x, y + 1.8 * s, x - 0.12 * s, y + 2.55 * s, 0.18 * s)], '#83a497', L, { style: 'flat' }),
        group([rbox(x, y + 0.35 * s, 0.46 * s, 0.35 * s, 0.07 * s)], '#c1aa94', L, { style: 'flat' }),
      ];
    case 'heart-tree': {
      const trunk = [cap(x - 0.7, y, x, y + 10.5 * s, 0.8 * s), cap(x, y + 5 * s, x - 7 * s, y + 4.55 * s, 0.3 * s), cap(x, y + 5.8 * s, x + 7 * s, y + 4.55 * s, 0.3 * s), cap(x, y + 8 * s, x - 3.8 * s, y + 7 * s, 0.22 * s), cap(x, y + 8 * s, x + 3.8 * s, y + 7 * s, 0.22 * s)];
      const leaves = [-5, -2.5, 0, 2.5, 5].map((dx, i) => disk(x + dx * s + Math.sin(time * 0.35 + i) * 0.06, y + (10.5 + (2 - Math.abs(i - 2)) * 0.7) * s, (2.1 + i % 2 * 0.4) * s));
      return [group(trunk, withAlpha('#514d3b', 0.48), L, { blend: 'smoothUnion', smoothK: 0.3, style: 'flat' }), group(leaves, withAlpha(theme.decor, 0.5), L, { blend: 'smoothUnion', smoothK: 0.4, style: 'flat' })];
    }
    case 'cargo-frame': {
      const frame = [cap(x - 3 * s, y, x - 3 * s, y + 10 * s, 0.2 * s), cap(x + 3 * s, y, x + 3 * s, y + 10 * s, 0.2 * s), cap(x - 3.4 * s, y + 10 * s, x + 3.4 * s, y + 10 * s, 0.27 * s), cap(x, y + 7.5 * s, x, y + 10 * s, 0.05 * s)];
      for (const side of [-1, 1]) for (let i = 0; i < 4; i++) frame.push(cap(x + side * 3 * s, y + i * 2.5 * s, x + side * 4.2 * s, y + (i + 1) * 2.5 * s, 0.07 * s));
      return [group(frame, withAlpha(theme.decor, 0.8), L, { style: 'flat' }), group([disk(x, y + 9.3 * s, 0.48 * s)], withAlpha(theme.accent, 0.35), L, { style: 'outline' })];
    }
    case 'courtyard-arch': {
      const arch = [cap(x - 6 * s, y, x - 6 * s, y + 3 * s, 0.45 * s), cap(x + 6 * s, y, x + 6 * s, y + 3 * s, 0.45 * s)];
      for (let i = 0; i < 10; i++) {
        const a = i * Math.PI / 10, b = (i + 1) * Math.PI / 10;
        arch.push(cap(x + Math.cos(a) * 6 * s, y + (3 + Math.sin(a) * 1.4) * s, x + Math.cos(b) * 6 * s, y + (3 + Math.sin(b) * 1.4) * s, 0.3 * s));
      }
      return [group(arch, withAlpha(theme.decor, 0.7), L, { style: 'flat' })];
    }
    case 'canopy': {
      const sway = Math.sin(time * 0.45 + seed) * 0.1;
      return [
        group([cap(x, y - 7 * s, x + 0.6 * s, y, 0.28 * s), cap(x + 0.4 * s, y - 1.5 * s, x - 2 * s, y + 0.7 * s, 0.15 * s)], color, L, { style: 'flat' }),
        group([disk(x + sway, y + s, 1.8 * s), disk(x - 1.6 * s + sway, y + 0.5 * s, 1.4 * s), disk(x + 1.8 * s + sway, y + 0.65 * s, 1.5 * s)], withAlpha(theme.decor, 0.6), L, { blend: 'smoothUnion', smoothK: 0.3, style: 'flat' }),
      ];
    }
    case 'sunbeam':
      return [group([tri(x, y, s * 0.035, s, Math.PI + 0.35, 0)], '#fff2b30b', L, { style: 'flat' }), group([tri(x + 3, y, s * 0.065, s, Math.PI + 0.35, 0)], '#fff2b30b', L, { style: 'flat' })];
    case 'gantry': {
      const width = 3.1 * s, height = 5.5 * s;
      const braces: Primitive[] = [cap(x - width, y, x - width, y + height, 0.13), cap(x + width, y, x + width, y + height, 0.13), cap(x - width, y + height, x + width, y + height, 0.18)];
      for (let i = -2; i <= 2; i++) braces.push(cap(x + i * s, y + height, x + (i + 0.5) * s, y + height - 0.6 * s, 0.045));
      return [group(braces, color, L, { style: 'flat' }), group([cap(x, y + height, x, y + height - 2 * s, 0.035)], theme.decorFar, L, { style: 'flat' }), group([disk(x - width, y + height + 0.15, 0.09)], withAlpha('#f5c677', 0.6 + Math.sin(time * 1.8 + seed) * 0.12), L, { style: 'flat', fx: 'glow', glow: 0.18 })];
    }
    case 'banner': {
      const sway = Math.sin(time * 1.3 + seed) * 0.05;
      return [group([cap(x - 0.85 * s, y, x + 0.85 * s, y, 0.065)], '#bda67c', L, { style: 'flat' }), group([rbox(x + sway, y - 1.1 * s, 0.62 * s, 1.08 * s, 0.025), tri(x + sway, y - 2.15 * s, 0.62 * s, 0.65 * s, Math.PI)], '#695575', L, { style: 'flat' }), group([cap(x + sway, y - 0.5 * s, x + sway, y - 1.6 * s, 0.055), cap(x - 0.25 * s, y - 0.82 * s, x + 0.25 * s, y - 0.82 * s, 0.055)], '#c9b581', L, { style: 'flat' })];
    }
    case 'torch': {
      const flicker = 1 + Math.sin(time * 7 + seed) * 0.07;
      return [group([cap(x, y - 0.5 * s, x, y, 0.08 * s)], color, L, { style: 'flat' }), group([disk(x, y + 0.13 * s, 0.17 * s * flicker), tri(x, y + 0.13 * s, 0.15 * s, 0.4 * s * flicker)], '#ffc782', L, { style: 'flat', fx: 'glow', glow: 0.75 * s }), group([disk(x, y + 0.14 * s, 0.08 * s)], '#fff0bc', L, { style: 'flat' })];
    }
    case 'tree': {
      const trunk = cap(x, y, x, y + 1.6 * s, 0.14 * s);
      const crown = [disk(x, y + 2.3 * s, 0.9 * s), disk(x - 0.6 * s, y + 1.9 * s, 0.65 * s), disk(x + 0.6 * s, y + 1.95 * s, 0.6 * s), disk(x, y + 2.9 * s, 0.55 * s)];
      return [group([trunk, ...crown], color, L, { blend: 'smoothUnion', smoothK: 0.3 * s, style: 'flat' })];
    }
    case 'pine': {
      const trunk = cap(x, y, x, y + 0.9 * s, 0.12 * s);
      const tiers = [tri(x, y + 0.6 * s, 1.1 * s, 1.6 * s), tri(x, y + 1.5 * s, 0.85 * s, 1.4 * s), tri(x, y + 2.3 * s, 0.6 * s, 1.2 * s)];
      return [group([trunk, ...tiers], color, L, { style: 'flat' })];
    }
    case 'bush':
      return [group([disk(x, y + 0.35 * s, 0.5 * s), disk(x - 0.45 * s, y + 0.25 * s, 0.35 * s), disk(x + 0.45 * s, y + 0.28 * s, 0.38 * s)], color, L, { blend: 'smoothUnion', smoothK: 0.25 * s, style: 'flat' })];
    case 'hill':
      return [group([disk(x, y - 2.2 * s, 3.4 * s), disk(x + 2.5 * s, y - 2.6 * s, 3 * s)], color, L, { blend: 'smoothUnion', smoothK: 1.2 * s, style: 'flat' })];
    case 'dune':
      return [group([disk(x, y - 3 * s, 4.2 * s), disk(x + 3.5 * s, y - 3.4 * s, 3.6 * s)], color, L, { blend: 'smoothUnion', smoothK: 2 * s, style: 'flat' })];
    case 'mesa':
      return [group([rbox(x, y + 1.2 * s, 1.6 * s, 1.3 * s, 0.25 * s), rbox(x, y + 2.7 * s, 1.1 * s, 0.35 * s, 0.15 * s), rbox(x - 2.4 * s, y + 0.7 * s, 1 * s, 0.8 * s, 0.2 * s)], color, L, { style: 'flat' })];
    case 'cactus':
      return [
        group(
          [cap(x, y, x, y + 1.7 * s, 0.17 * s), cap(x - 0.05 * s, y + 0.9 * s, x - 0.5 * s, y + 0.95 * s, 0.11 * s), cap(x - 0.5 * s, y + 0.95 * s, x - 0.5 * s, y + 1.35 * s, 0.11 * s), cap(x + 0.05 * s, y + 0.6 * s, x + 0.45 * s, y + 0.65 * s, 0.11 * s), cap(x + 0.45 * s, y + 0.65 * s, x + 0.45 * s, y + 1.1 * s, 0.11 * s)],
          color,
          L,
          { blend: 'smoothUnion', smoothK: 0.1 * s, style: 'flat' },
        ),
      ];
    case 'gear': {
      const r = 1.1 * s;
      const spin = time * (seed % 2 ? 0.4 : -0.3) + seed;
      const teeth: Primitive[] = [];
      for (let i = 0; i < 8; i++) {
        const a = spin + (i / 8) * Math.PI * 2;
        teeth.push(rbox(x + Math.cos(a) * r, y + 2 * s + Math.sin(a) * r, 0.22 * s, 0.32 * s, 0.05, a - Math.PI / 2));
      }
      return [group([disk(x, y + 2 * s, r), ...teeth], color, L, { style: 'flat' }), group([disk(x, y + 2 * s, r * 0.32)], theme.decorFar, L, { style: 'flat' })];
    }
    case 'stack':
      return [group([rbox(x, y + 2.6 * s, 0.5 * s, 2.8 * s, 0.1), rbox(x, y + 5.4 * s, 0.65 * s, 0.25 * s, 0.08)], color, L, { style: 'flat' }), group([disk(x + 0.3 * s + Math.sin(time * 0.7 + seed) * 0.3, y + 6.4 * s, 0.55 * s), disk(x + 0.9 * s + Math.sin(time * 0.5 + seed) * 0.4, y + 7.2 * s, 0.75 * s)], withAlpha(theme.decorFar, 0.6), L, { blend: 'smoothUnion', smoothK: 0.5, style: 'flat' })];
    case 'pipe':
      return [group([cap(x - 2 * s, y + 1.2 * s, x + 2 * s, y + 1.2 * s, 0.22 * s), cap(x + 2 * s, y + 1.2 * s, x + 2 * s, y + 3 * s, 0.22 * s), disk(x + 2 * s, y + 1.2 * s, 0.34 * s)], color, L, { style: 'flat' })];
    case 'tower': {
      const crenels: Primitive[] = [];
      for (let i = -1; i <= 1; i++) crenels.push(rbox(x + i * 0.55 * s, y + 3.75 * s, 0.2 * s, 0.25 * s, 0.03));
      return [group([rbox(x, y + 1.8 * s, 0.8 * s, 1.9 * s, 0.08), ...crenels], color, L, { style: 'flat' })];
    }
    case 'moon':
      return [group([disk(x, y, 1.1 * s)], withAlpha('#fff6d8', 0.85), L, { style: 'flat', fx: 'glow', glow: 1.4 * s })];
    case 'sun':
      return [group([disk(x, y, 1.3 * s)], withAlpha('#fff1b8', 0.9), L, { style: 'flat', fx: 'glow', glow: 1.8 * s })];
    case 'star':
      return [group([disk(x, y, 0.045 * s)], withAlpha('#dfe8ff', 0.5 + 0.5 * Math.abs(Math.sin(time * 1.3 + seed))), L, { style: 'flat' })];
    case 'cloud':
      return [group([disk(x, y, 0.7 * s), disk(x + 0.8 * s, y + 0.1 * s, 0.9 * s), disk(x + 1.7 * s, y, 0.65 * s), rbox(x + 0.8 * s, y - 0.3 * s, 1.5 * s, 0.35 * s, 0.3 * s)], withAlpha('#ffffff', 0.35), L, { blend: 'smoothUnion', smoothK: 0.4, style: 'flat' })];
    case 'bat': {
      const flap = Math.sin(time * 9 + seed) * 0.25;
      const bx = x + Math.sin(time * 0.6 + seed) * 2;
      const by = y + Math.cos(time * 0.8 + seed) * 0.6;
      return [group([disk(bx, by, 0.12 * s), tri(bx - 0.28 * s, by, 0.3 * s, 0.35 * s, Math.PI / 2 + flap), tri(bx + 0.28 * s, by, 0.3 * s, 0.35 * s, -Math.PI / 2 - flap)], '#0c0710', L, { style: 'flat' })];
    }
    case 'grave':
      return [group([rbox(x, y + 0.55 * s, 0.35 * s, 0.55 * s, 0.3 * s), rbox(x, y + 0.1 * s, 0.45 * s, 0.1 * s, 0.05)], color, L, { style: 'flat' })];
    case 'ember': {
      const t = (time * 0.35 + seed * 0.13) % 1;
      const ex = x + Math.sin(time * 0.9 + seed) * 0.6;
      const ey = y + t * 6 - 3;
      return [group([disk(ex, ey, 0.06 * s)], withAlpha('#ffb347', 0.6 * (1 - t)), L, { style: 'flat', fx: 'glow', glow: 0.25 })];
    }
    case 'stalagmite':
      return [group([tri(x, y - 0.2, 0.9 * s, 3.2 * s), tri(x + 1.3 * s, y - 0.2, 0.6 * s, 2.1 * s), tri(x - 1.1 * s, y - 0.2, 0.5 * s, 1.6 * s)], color, L, { style: 'flat' })];
    case 'snow': {
      const t = (time * 0.12 + seed * 0.07) % 1;
      const sx = x + Math.sin(time * 0.7 + seed) * 0.8;
      const sy = y + 6 - t * 14;
      return [group([disk(sx, sy, 0.07 * s)], withAlpha('#ffffff', 0.55), L, { style: 'flat' })];
    }
    case 'grid': {
      // One group per line: bundled, every pixel of the arena would evaluate all 26 lines, whereas a
      // line on its own only touches the thin strip it covers. Crossings blend twice, which reads as
      // a slightly brighter node and suits the look.
      const out: ShapeGroup[] = [];
      const half = s / 2;
      const color = withAlpha(theme.trim, 0.25);
      for (let i = -6; i <= 6; i++) {
        out.push(group([cap(x - half, y + i * 2, x + half, y + i * 2, 0.012)], color, L, { style: 'flat', pad: 0.05 }));
        out.push(group([cap(x + i * 2.5, y - half, x + i * 2.5, y + half, 0.012)], color, L, { style: 'flat', pad: 0.05 }));
      }
      return out;
    }
    default:
      return [group([cap(x, y, x, y + 1.6 * s, 0.12 * s)], color, L, { style: 'flat' })];
  }
}
